<template>
  <div class="ateco-view">
    <button class="close-button" @click="close">×</button>
    <h2>Selezione ATECO</h2>

    <!-- Mostra il messaggio di errore -->
    <div v-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <!-- Campo di ricerca con pulsante di cancellazione -->
    <div class="search-box">
      <div class="input-container">
        <input type="text" v-model="searchQuery" placeholder="Cerca ATECO..." @input="filterAtecoList" />
        <button v-if="searchQuery" class="clear-button" @click="clearSearch">
          ×
        </button>
      </div>
    </div>

    <!-- Contenitore per la lista dei risultati con scrollbar -->
    <div class="results-container">
      <div v-if="filteredAtecoList.length > 0">
        <TreeNode v-for="node in filteredTreeData" :key="node.CODICE + searchQuery" :node="node"
          :selectedCodes="selectedCodes" @update-selection="updateSelection" />
      </div>

      <!-- Messaggio se non ci sono ATECO disponibili -->
      <div v-if="atecoList.length === 0">
        <p>Nessun ATECO disponibile.</p>
        <button class="load-ateco-button" @click="loadAteco">Carica Lista Ateco</button>
      </div>
    </div>

    <!-- Pulsante per cercare i codici ATECO -->
    <div v-if="hasSelectedCodes" class="save-button-container">
      <button class="save-button" @click="searchAzienda">Cerca Aziende</button>
    </div>
  </div>
</template>

<script>
import TreeNode from "@/components/TreeNode.vue";
import "@/assets/css/atecoView.css";

export default {
  name: "AtecoView",
  props: {
    atecoList: {
      type: Array,
      required: true,
    },
    selectedAteco: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: "", // Messaggio di errore
    },
  },
  components: {
    TreeNode,
  },
  data() {
    return {
      searchQuery: "", // Testo di ricerca
      selectedCodes: [], // Codici ATECO selezionati
    };
  },
  computed: {
    // Normalizza la query di ricerca
    normalizedQuery() {
      return this.searchQuery.trim().toLowerCase();
    },

    // Filtra atecoList in base alla query di ricerca
    filteredAtecoList() {
      if (!this.normalizedQuery) {
        return this.atecoList;
      }

      const query = this.normalizedQuery;
      const filtered = this.atecoList.filter((item) => {
        const descrizioneMatch = item.DESCRIZIONE.toLowerCase().includes(query);
        const codiceMatch = item.CODICE.toLowerCase().includes(query);
        return descrizioneMatch || codiceMatch;
      });

      // Aggiungi i padri mancanti solo se non sono già presenti
      const parentCodes = new Set();
      filtered.forEach((item) => {
        if (item.CODICE_PADRE && item.CODICE_PADRE !== item.CODICE) {
          parentCodes.add(item.CODICE_PADRE);
        }
      });

      const missingParents = this.atecoList.filter((item) =>
        parentCodes.has(item.CODICE) && !filtered.some((f) => f.CODICE === item.CODICE)
      );

      // Combina i risultati e ordina per CODICE
      const combinedResults = [...filtered, ...missingParents];
      combinedResults.sort((a, b) => a.CODICE.localeCompare(b.CODICE));

      return combinedResults;
    },

    // Costruisce l'albero gerarchico con i risultati filtrati
    filteredTreeData() {
      const map = {};
      const tree = [];

      // Passo 1: Crea una mappa di tutti i nodi
      this.filteredAtecoList.forEach((item) => {
        map[item.CODICE] = { ...item, children: [] };
      });

      // Passo 2: Costruisci l'albero
      this.filteredAtecoList.forEach((item) => {
        if (!item.CODICE_PADRE || item.CODICE_PADRE === item.CODICE) {
          // Nodo radice
          tree.push(map[item.CODICE]);
        } else if (map[item.CODICE_PADRE]) {
          // Nodo figlio
          map[item.CODICE_PADRE].children.push(map[item.CODICE]);
        } else {
          console.warn(`Nodo padre mancante per: ${item.CODICE}. Aggiungendo un nodo placeholder.`);
          map[item.CODICE_PADRE] = {
            CODICE: item.CODICE_PADRE,
            DESCRIZIONE: "",
            isPlaceholder: true, // Identifica il nodo come placeholder
            children: [],
          };
          tree.push(map[item.CODICE_PADRE]);
          map[item.CODICE_PADRE].children.push(map[item.CODICE]);
        }
      });

      return tree;
    },

    // Verifica se ci sono codici selezionati
    hasSelectedCodes() {
      return this.selectedCodes.length > 0;
    },
  },
  methods: {
    // Aggiorna la selezione
    updateSelection(selectedCodes) {
      this.selectedCodes = selectedCodes;
      this.$emit("select-ateco", selectedCodes);
    },

    // Carica gli ATECO
    async loadAteco() {
      try {
        this.$emit("load-ateco");
      } catch (error) {
        console.log("errore: ", error);
        this.$emit("update-error", "Errore durante il caricamento degli ATECO. Riprova più tardi.");
      }
    },

    // Chiude la vista ATECO
    close() {
      this.$emit("update-error","");
      this.$emit("closeAteco");
    },

    // Cancella il contenuto del campo di ricerca
    clearSearch() {
      this.searchQuery = "";
    },

    // Salva i file e stampa i codici selezionati
    async searchAzienda() {
      try {
        this.$emit("search-ateco", this.selectedCodes);
      } catch (error) {
        this.$emit("update-error", "Errore durante la ricerca degli ATECO. Controlla i dati e riprova.");
      }
    },
  },
  watch: {
    // Watcher per la prop selectedAteco
    selectedAteco(newSelectedAteco) {
      this.selectedCodes = newSelectedAteco.map((ateco) => ateco.CODICE);
    },
  },
};
</script>

<style scoped>
.alert.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 5px;
}
</style>
